// DS: C44
/** Popover showing information about a hovered user */
import React, { useContext } from 'react';
import classnames from 'classnames';
import { compose } from 'redux';
import { PapiDaSharedUserInfo } from '@wix/da-papi-types';
import UserAvatarPopoverContent from './UserAvatarPopoverContent';
import {
  withLogBiEvent,
  WithLogBiEventProps,
} from '@wix/da-bi/pkg/hocs/withLogBiEvent';
import UserAvatarPopoverButtonsContext from './UserAvatarPopoverButtonsContext';

import {
  WithMobileContextProps,
  withMobileContext,
} from '@wix/da-react-context/pkg/MobileContext';
import {
  BiContext,
  BiLoggerContextProvider,
} from '@wix/da-bi/pkg/BiLogger.context';
import type { BiEvent } from '@wix/da-bi/pkg/events';
import SimpleHoverPopperPopover from '@wix/da-ds/pkg/popper/presets/SimpleHoverPopperPopover';

import s from './UserAvatarPopover.scss';

const HOVER_DELAY = 500;

export interface Props {
  isInitialized: boolean;
  isLoading?: boolean;
  isError?: boolean;
  isOwner?: boolean;
  username: string;
  data?: PapiDaSharedUserInfo;
  className?: string;
  biData?: BiEvent;
  relatedItemBiData?: any;
  initialize: (username: string) => void;
  children: React.ReactNode;
}

export const UserAvatarPopover: React.FC<
  Props & WithMobileContextProps & WithLogBiEventProps
> = props => {
  const {
    data,
    isError,
    isOwner,
    isLoading,
    children,
    className,
    isMobile,
    relatedItemBiData,
  } = props;

  const PopoverContent = () => {
    const biContext = useContext(BiContext);
    const withButtons = useContext(UserAvatarPopoverButtonsContext);
    if (isError || !data) {
      return null;
    }
    return (
      <div
        className={classnames(
          s['tooltip'],
          (!withButtons || isOwner) && s['without-buttons']
        )}
      >
        {!isError && data && (
          <BiLoggerContextProvider
            value={{
              widgetname: 'user_popup',
              sectionname:
                biContext.sectionname && `${biContext.sectionname}_popup`,
            }}
          >
            <UserAvatarPopoverContent
              withButtons={!isOwner && withButtons}
              relatedItemBiData={relatedItemBiData}
              {...data}
            />
          </BiLoggerContextProvider>
        )}
      </div>
    );
  };

  const onVisible = () => {
    if (props.biData) {
      props.logBiEvent(props.biData);
    }

    if (!props.isInitialized) {
      props.initialize(props.username);
    }
  };

  if (isMobile) {
    return <>{children}</>;
  }

  return (
    <SimpleHoverPopperPopover
      className={classnames(
        s['popover-root'],
        isLoading && s['loading'],
        className
      )}
      contentClassName={s['tooltip-content']}
      arrowClassName={s['tooltip-arrow']}
      popoverContent={<PopoverContent />}
      noPadding={true}
      hoverDelay={HOVER_DELAY}
      isVisible={!!data}
      onVisible={onVisible}
      showArrow={false}
    >
      {children}
    </SimpleHoverPopperPopover>
  );
};

export default compose(withMobileContext, withLogBiEvent)(UserAvatarPopover);
